import { type ColumnDef, getCoreRowModel, useReactTable, type VisibilityState } from '@tanstack/react-table';
import type { UseQueryResult } from '@tanstack/react-query';
import { IPagination } from '@/components/table/use-pagination.ts';
import { IPaginatedResponse } from '@/services/api.ts';

function getData(data: any, query?: UseQueryResult<any>) {
  if (query?.data?.items) {
    return query.data.items;
  }
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.data) return data.data;
  return [];
}

function getRowCount(data: any, query?: UseQueryResult<any>) {
  if (query?.data?.pagination) {
    return query.data.pagination?.total || 0;
  }
  if (!data) return 0;
  if (Array.isArray(data)) return data.length;
  return data?.pagination?.total || 0;
}

export type UseAppTableReturn<T> = {
  table: ReturnType<typeof useReactTable<T>>;
  pagination?: IPagination;
  refetch?: () => void;
  query?: UseQueryResult<any>;
  pageSizeOptions?: number[];
  paginationClassName?: string;
};

type Props<T> = {
  columns: ColumnDef<T>[];
  data?: IPaginatedResponse<T> | T[];
  pagination?: IPagination;
  initialState?: any;
  query?: UseQueryResult<any>;
  pageSizeOptions?: number[];
  columnVisibility?: VisibilityState;
  paginationClassName?: string;
};

export function useAppTable<T>({
  columns,
  data,
  pagination,
  initialState,
  query,
  pageSizeOptions,
  columnVisibility,
  paginationClassName,
}: Props<T>): UseAppTableReturn<T> {
  const table = useReactTable<T>({
    columns,
    data: getData(data, query),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: getRowCount(data, query),
    state: {
      pagination: pagination?.pagination,
      sorting: pagination?.sorting,
      columnVisibility,
    },
    onSortingChange: pagination?.setSorting,
    onPaginationChange: pagination?.setPagination,
    initialState: initialState,
  });
  return {
    table,
    pagination,
    query,
    pageSizeOptions,
    paginationClassName,
  };
}
